import React, { useEffect, useState } from "react";
import classes from "./OrderStatus.module.css";
import { useSelector } from "react-redux";
import useLang from "../hooks/useLang";
import { getUserRoleFromToken } from "../utils/Auth";
import { userRoles } from "../types/enums";
import { CONFIG } from "../config";
import useCurrencies from "../hooks/useCurrencies";
import { CURRENCY_EUR } from "../api/currency";
import { currencySymbol } from "../utils/formatters";
import CurrencyBadge from "./CurrencyBadge";

/**
 * Company rows keep their original bare formatting on purpose: that figure is a mixed LEK+EUR
 * scalar from `SelectCompanyFinance_sp`, and this row stays untouched until the API splits it.
 * Client rows keep the grouped, space-separated form they had when `DualMoney` rendered them.
 */
const formatTile = (value?: number | null, currencyId?: number | null) => {
	if (!currencyId) {
		return `${value?.toFixed(2) ?? (0.0).toFixed(2)}${CONFIG?.CURRENCY}`;
	}
	return `${new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	}).format(value ?? 0)} ${currencySymbol(currencyId)}`;
};

type OrderStatusProps = {
	orderData?: any[];
	descProp?: string;
	numberProp?: string;
	sticky?: boolean;
	stickyTop?: string;
};

type Tile = {
	description: string;
	value?: number | null;
};

/**
 * A row of tiles, all denominated in one currency.
 *
 * `currencyId` is null for the company figures, which are a mixed scalar the API has not split —
 * see the comment on `companyTiles`.
 */
type TileRow = {
	key: string;
	currencyId?: number | null;
	tiles: Tile[];
};

const FinanceStatistic: React.FC<OrderStatusProps> = ({
	sticky = false,
	stickyTop = "0px",
}) => {
	const [userRole, setUserRole] = useState<any | undefined>();
	const reduxData = useSelector(
		(state: any) => state.finance.companyFinanceData
	);
	const { dualCurrencyEnabled, defaultCurrencyId } = useCurrencies();

	useEffect(() => {
		const userRoleId = getUserRoleFromToken();
		setUserRole(userRoleId);
	}, []);

	const { strings } = useLang("financeStatistics");

	/**
	 * The seller's five figures, in one currency.
	 *
	 * All five are split by currency by SelectClientFinance_sp: a seller is settled in the currency
	 * each order was REGISTERED in, so the LEK and EUR sets are two independent balances. They used
	 * to share a row, with EUR rendered small beneath its LEK twin — which read as a footnote on a
	 * single number. A full second row, identical in every respect but the currency badge, says
	 * what they actually are: two balances of equal standing that are never added together.
	 */
	const clientTilesFor = (suffix: "" | "Eur"): Tile[] => [
		{
			description: strings?.descriptions.currentBalance,
			value: reduxData?.[`currentBalance${suffix}`],
		},
		{
			description: strings?.descriptions.salesBalance,
			value: reduxData?.[`salesBalance${suffix}`],
		},
		{
			description: strings?.descriptions.refusals,
			value: reduxData?.[`refusals${suffix}`],
		},
		{
			description: strings?.descriptions.totalBalance,
			value: reduxData?.[`totalBalance${suffix}`],
		},
		{
			description: strings?.descriptions.currentUnsettled,
			value: reduxData?.[`currentUnsettled${suffix}`],
		},
	];

	// Admin/company figures come from SelectCompanyFinance_sp, which is NOT dual-currency aware —
	// it sums LEK and EUR into one scalar. Deliberately left with no currency badge and no second
	// row: labelling a mixed number with a currency would state something false. Tracked separately.
	const companyTiles: Tile[] = [
		{
			description: strings?.descriptions.orderPackagePrice,
			value: reduxData?.orderPackagePrice,
		},
		{
			description: strings?.descriptions.transportPrice,
			value: reduxData?.transportPrice,
		},
		{
			description: strings?.descriptions.refusalOrderPackagePrice,
			value: reduxData?.refusalOrderPackagePrice,
		},
		{
			description: strings?.descriptions.processedSettlementValues,
			value: reduxData?.processedSettlementValues,
		},
		{
			description: strings?.descriptions.allExpenses,
			value: reduxData?.allExpenses,
		},
		{
			description: strings?.descriptions.currentCash,
			value: reduxData?.currentCash,
		},
		{
			description: strings?.descriptions.unConfirmedSettlementValues,
			value: reduxData?.unConfirmedSettlementValues,
		},
	];

	const isClient = userRole === userRoles.CLIENT;

	const rows: TileRow[] = isClient
		? [
				{
					key: "local",
					currencyId: defaultCurrencyId,
					tiles: clientTilesFor(""),
				},
				...(dualCurrencyEnabled
					? [
							{
								key: "eur",
								currencyId: CURRENCY_EUR,
								tiles: clientTilesFor("Eur"),
							},
					  ]
					: []),
		  ]
		: [{ key: "company", tiles: companyTiles }];

	// The role is read from the token in an effect, so the first render has none. Rendering the
	// tiles anyway meant a client saw the seven COMPANY figures flash before their own five
	// replaced them.
	if (userRole === undefined) return null;

	return (
		<>
			<div
				className={classes.financeStatusContainer}
				style={{
					position: sticky ? "sticky" : "static",
					top: sticky ? stickyTop : "auto",
					zIndex: sticky ? 100 : "auto",
				}}
			>
				<div className={classes.firstStatusForFinance}>
					{rows.map((row) => (
						<div className={classes.financeRow} key={row.key}>
							{/* Only rendered when there is more than one row: with a single
							    currency there is nothing to tell apart, and a badge would just
							    add noise to Kosovo and Macedonia. */}
							{rows.length > 1 && (
								<div className={classes.financeRowHeader}>
									<CurrencyBadge
										currencyId={row.currencyId}
									/>
								</div>
							)}
							<div className={classes.mainStatusForFinance}>
								{row.tiles.map((tile: Tile, index: number) => (
									<div
										className={`${classes.oneDiv} ${
											index === row.tiles.length - 1
												? classes.noBorder
												: ""
										}`}
										key={index}
									>
										<p className={classes.statusDesc}>
											{tile.description}
										</p>
										<h1 className={classes.statusNumber}>
											{formatTile(
												tile.value,
												row.currencyId
											)}
										</h1>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</>
	);
};

export default FinanceStatistic;
